import React from 'react';
import { User, Phone, Mail } from 'lucide-react';

const calculateAge = (birthdate) => {
  const today = new Date();
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const PatientInfoCard = ({ patient }) => {
  const id = patient.identifying_data;

  return (
    <div className="mb-6">
      <div className="card border-2 border-slate-200 bg-white shadow-lg">
        <h2 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-slate-100 flex items-center gap-2">
          <User size={20} className="text-slate-600" />
          Patient Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="flex justify-center md:col-span-1">
            {id.profile_photo ? (
              <img
                src={id.profile_photo}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-slate-200 shadow-md"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 border-4 border-white shadow-md flex items-center justify-center">
                <span className="text-3xl font-bold text-white">
                  {id.first_name?.charAt(0) || '?'}
                </span>
              </div>
            )}
          </div>

          <div className="md:col-span-3 grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Full Name</p>
              <p className="text-slate-900 font-medium" data-testid="patient-full-name">
                {[id.first_name, id.middle_name, id.last_name].filter(Boolean).join(' ') || 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Nickname</p>
              <p className="text-slate-900">{id.nickname}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Birthdate</p>
              <p className="text-slate-900">{id.birthdate}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Age</p>
              <p className="text-slate-900">{calculateAge(id.birthdate)} years</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Sex Assigned at Birth</p>
              <p className="text-slate-900">{id.sex}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Pronouns</p>
              <p className="text-slate-900">{id.pronouns || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Home Address</p>
              <p className="text-slate-900">{id.home_address}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Occupation</p>
              <p className="text-slate-900">{id.occupation || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Employer</p>
              <p className="text-slate-900">{id.employer_name || 'Not provided'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Work Address</p>
              <p className="text-slate-900">{id.work_address || 'Not provided'}</p>
            </div>
          </div>

          {/* Contact Information - Mobile-First Clickable Phone and Email */}
          <div className="mt-6 pt-4 border-t-2 border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">Quick Contact</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              {id.mobile_number && (
                <a
                  href={`tel:${id.mobile_number}`}
                  data-testid="quick-contact-call-btn"
                  className="flex items-center justify-center gap-3 w-full sm:w-auto px-5 py-3.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 active:from-green-700 active:to-green-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-medium text-base"
                >
                  <Phone size={20} />
                  <span className="sm:hidden">Call</span>
                  <span className="hidden sm:inline">Call {id.mobile_number}</span>
                </a>
              )}
              {id.email && (
                <a
                  href={`mailto:${id.email}`}
                  data-testid="quick-contact-email-btn"
                  className="flex items-center justify-center gap-3 w-full sm:w-auto px-5 py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:from-blue-700 active:to-blue-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-medium text-base"
                >
                  <Mail size={20} />
                  <span className="sm:hidden">Email</span>
                  <span className="hidden sm:inline">Email {id.email}</span>
                </a>
              )}
              {!id.mobile_number && !id.email && (
                <p className="text-slate-500 italic text-sm">No contact information available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientInfoCard;
